import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { AssessmentQuestion } from "@/components/AssessmentQuestion";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, PenLine, ShieldCheck, Circle } from "lucide-react";

export default function AssessmentReview() {
  const [defs, setDefs] = useState(null);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savingLevel, setSavingLevel] = useState(false);
  const [openSections, setOpenSections] = useState([]);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const laneParam = params.get("lane");

  const load = async () => {
    try {
      const s = await api.get("/assessment/state");
      const lane = laneParam || s.data.progress?.lane || null;
      const d = await api.get("/assessment/definitions" + (lane ? `?lane=${lane}` : ""));
      setDefs(d.data);
      setState(s.data);
    } catch (err) {
      setError(extractApiError(err, "Failed to load."));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [laneParam]);

  const answered = useMemo(() => {
    if (!state || !defs) return 0;
    return defs.questions.filter((q) => {
      const a = state.answers[q.id];
      return a && !a.skipped && a.answer !== null && a.answer !== undefined && a.answer !== "";
    }).length;
  }, [state, defs]);

  const unansweredList = useMemo(() => {
    if (!state || !defs) return [];
    return defs.questions.filter((q) => {
      const a = state.answers[q.id];
      return !a || (!a.skipped && (a.answer === null || a.answer === undefined || a.answer === ""));
    });
  }, [state, defs]);

  const saveAnswer = async (qid, value, skipped) => {
    try {
      const { data } = await api.put("/assessment/answers", {
        answers: [{ question_id: qid, answer: value, skipped }],
      });
      setState(data);
    } catch (_) { /* ignore */ }
  };

  const adjustDcl = async (v) => {
    setSavingLevel(true);
    try {
      await api.put("/assessment/progress", {
        adjusted_digital_confidence_level: v,
      });
      const { data } = await api.get("/assessment/state");
      setState(data);
    } finally { setSavingLevel(false); }
  };

  if (!defs || !state) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="review-loading">
          Loading review…
        </div>
      </AppShell>
    );
  }

  const dcl = state.digital_confidence;
  const effective = dcl.adjusted_level || dcl.level;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-wider text-fg-secondary">Review</p>
        <h1
          data-testid="review-title"
          className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          Your answers, in your own words
        </h1>
        <p className="mt-3 text-sm text-fg-secondary">
          You can edit anything before you submit. You can also submit and edit later — nothing is locked.
        </p>
        <p className="mt-2 text-sm text-fg-secondary" data-testid="review-count">
          {answered} of {defs.questions.length} answered.
        </p>

        {unansweredList.length > 0 && (
          <section
            data-testid="review-unanswered"
            className="mt-4 rounded-md border border-accent-warm/30 bg-accent-warm/5 p-4"
          >
            <p className="text-sm font-medium text-fg-primary flex items-center gap-2">
              <Circle className="h-3.5 w-3.5" aria-hidden="true" />
              You haven't answered {unansweredList.length} question{unansweredList.length === 1 ? "" : "s"} yet
            </p>
            <p className="mt-1 text-xs text-fg-secondary">
              These are OK to leave blank — click any to open it directly.
            </p>
            <ul className="mt-3 space-y-1 text-sm">
              {unansweredList.map((q) => (
                <li key={q.id} data-testid={`review-unanswered-${q.id}`}>
                  <button
                    type="button"
                    data-testid={`review-unanswered-${q.id}-btn`}
                    onClick={() => {
                      // Open the parent section's accordion (if not already
                      // open) AND put the question into editing mode, then
                      // scroll to it so the participant lands on the input.
                      setOpenSections((prev) => prev.includes(q.section) ? prev : [...prev, q.section]);
                      setEditingId(q.id);
                      requestAnimationFrame(() => {
                        const el = document.querySelector(`[data-testid="review-item-${q.id}"]`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                      });
                    }}
                    className="text-left text-fg-primary underline hover:text-accent-warm"
                  >
                    {q.prompt}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && (
          <Alert variant="destructive" className="mt-6" data-testid="review-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Digital confidence level */}
        {dcl.complete && (
          <section
            data-testid="review-digital-confidence"
            className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-progress-teal/10 text-progress-teal">
                <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-fg-secondary">AI & digital starting point</p>
                <h2 className="text-lg font-heading font-medium text-fg-primary">
                  Level {effective}: {defs.digital_confidence_levels[effective - 1].name}
                </h2>
              </div>
            </div>
            <p className="mt-3 text-sm text-fg-secondary">
              {defs.digital_confidence_levels[effective - 1].description}
            </p>
            <p className="mt-2 text-xs text-fg-muted">{dcl.explanation}</p>
            <div className="mt-4">
              <Label className="text-xs text-fg-secondary">Adjust your level (optional)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Slider
                  min={1} max={5} step={1}
                  value={[effective]}
                  onValueChange={([v]) => adjustDcl(v)}
                  className="[&_[role=slider]]:bg-accent-warm [&_[role=slider]]:border-accent-warm max-w-md"
                  data-testid="review-dcl-slider"
                />
                <span className="text-sm text-fg-primary tabular-nums">{effective}</span>
                {savingLevel && <span className="text-xs text-fg-secondary">Saving…</span>}
              </div>
              {dcl.adjusted_level && dcl.adjusted_level !== dcl.level && (
                <p className="mt-2 text-xs text-fg-secondary">
                  You adjusted this from the computed level {dcl.level}. Both are stored.
                </p>
              )}
            </div>
          </section>
        )}

        {/* By section, accordion */}
        <div className="mt-8">
          <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-3">
            {defs.sections.map((s) => {
              const qs = defs.questions.filter((q) => q.section === s.id);
              const answeredInSection = qs.filter((q) => {
                const a = state.answers[q.id];
                return a && !a.skipped && a.answer !== null && a.answer !== undefined && a.answer !== "";
              }).length;
              return (
                <AccordionItem
                  key={s.id}
                  value={s.id}
                  data-testid={`review-section-${s.id}`}
                  className="border border-border-subtle rounded-lg bg-bg-surface px-4"
                >
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex-1 flex items-center justify-between pr-4">
                      <span className="text-fg-primary font-heading font-medium">{s.index + 1}. {s.title}</span>
                      <span className="text-xs text-fg-secondary">{answeredInSection} / {qs.length}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2">
                      {qs.map((q) => {
                        const a = state.answers[q.id];
                        const editing = editingId === q.id;
                        return (
                          <div key={q.id} className="rounded-md border border-border-subtle bg-bg-base p-4" data-testid={`review-item-${q.id}`}>
                            <p className="text-sm font-medium text-fg-primary">{q.prompt}</p>
                            {editing ? (
                              <div className="mt-3">
                                <AssessmentQuestion
                                  question={q}
                                  answer={a?.answer}
                                  skipped={!!a?.skipped}
                                  onSave={(value, skipped) => saveAnswer(q.id, value, skipped)}
                                  onSkip={(sk) => saveAnswer(q.id, a?.answer ?? null, sk)}
                                />
                                <div className="mt-3 flex justify-end">
                                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)} data-testid={`review-done-${q.id}`}
                                    className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover">
                                    Done editing
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 flex items-start gap-3 justify-between">
                                <div className="text-sm text-fg-secondary whitespace-pre-line flex-1">
                                  <AnswerSummary question={q} answer={a?.answer} skipped={!!a?.skipped} />
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => setEditingId(q.id)} data-testid={`review-edit-${q.id}`}
                                  className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                                  <PenLine className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                                  Edit
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link to={`/assessment${state.progress?.lane ? `?lane=${state.progress.lane}` : ""}`}>
            <Button variant="ghost" data-testid="review-back-btn" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to overview
            </Button>
          </Link>
          <Button
            onClick={() => {
              const lane = state.progress?.lane || laneParam;
              navigate(`/assessment/consent${lane ? `?lane=${lane}` : ""}`);
            }}
            data-testid="review-continue-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            Continue to submit
            <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </AppShell>
  );
}


function AnswerSummary({ question, answer, skipped }) {
  if (skipped) return <span className="italic text-fg-muted">Marked skipped.</span>;
  if (answer === null || answer === undefined || answer === "") {
    return <span className="italic text-fg-muted">Not answered yet.</span>;
  }
  if (question.type === "text") return <span>{answer}</span>;
  if (question.type === "single") {
    const opt = question.options.find((o) => o.value === answer);
    return <span>{opt?.label || answer}</span>;
  }
  if (question.type === "multi") {
    if (!Array.isArray(answer) || answer.length === 0) return <span className="italic text-fg-muted">None picked.</span>;
    const labels = answer.map((v) => question.options.find((o) => o.value === v)?.label || v);
    return <span>{labels.join(" · ")}</span>;
  }
  if (question.type === "scale") return <span>You picked {answer} out of {question.scale_max}.</span>;
  if (question.type === "strengths_with_examples") {
    if (!Array.isArray(answer) || answer.length === 0) return <span className="italic text-fg-muted">None picked.</span>;
    return (
      <ul className="list-disc pl-5 space-y-1">
        {answer.map((s) => (
          <li key={s.strength}>
            <span className="text-fg-primary">{s.strength.replace(/_/g, " ")}</span>
            {s.example ? <span className="text-fg-secondary"> — “{s.example}”</span> : <span className="italic text-fg-muted"> — no example (weaker evidence)</span>}
          </li>
        ))}
      </ul>
    );
  }
  return <span>{JSON.stringify(answer)}</span>;
}
